import { ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Campaign } from '../../models/campaign';
import { CampaignService } from '../../services/campaign.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';

@Component({
    selector: 'app-highlights',
    templateUrl: './highlights.component.html',
    styleUrls: ['./highlights.component.scss']
})
export class HighlightsComponent implements OnChanges, OnDestroy {
    highlights = {
        locations: 0,
        channels: 0,
        screens: 0,
        slots: 0,
        avgCPM: 0,
        plays: 0,
        totalImpressions: 0,
        totalAttentiveImpressions: 0,
        totalReaches: 0,
        // Which source the audience figures came from: the provider reports plays, the local
        // estimate reports reach, so the middle metric of the CPM box follows this.
        externalMetricsSource: false,
        campaignBudget: {
            value: 0,
            currencyId: 1,
            currencyName: 'USD Dollar',
            currencyCode: 'USD'
        },
        campaignTotalPrice: {
            currencyId: 1,
            currencyName: 'USD Dollar',
            currencyCode: 'USD',
            value: 0
        },
        nonCappedCampaignTotalPrice: {
            currencyId: 1,
            currencyName: 'USD Dollar',
            currencyCode: 'USD',
            value: 0
        },
        // Not rendered here — kept only as the signal that the provider answered, which is what
        // decides whether the Performance Insights drawer is offered at all.
        externalData: null as any[]
    };
    isLoading = false;
    isSupplier = this.authenticationService.isSupplier();
    isClient = this.authenticationService.isClient();

    readonly SELECTION_END_STEP = 2;
    readonly METRICS_START_STEP = 3;
    readonly BUDGET_START_STEP = 3;

    isLocationScreensGreyedOut = true;
    isSlotPlaysGreyedOut = true;
    isBudgetPriceGreyedOut = true;

    OverBudget = false;
    OverBudgetGreyedOut = false;
    underBudgetGreyedOut = false;

    // Signature of the last schedule we fetched metrics for. ngOnChanges fires on every step
    // navigation (the `step` input changes), but the metrics only change when the schedule does —
    // so we skip the backend call unless this signature changes.
    private lastMetricsSignature: string | null = null;

    // The metrics call in flight. A quicker edit cancels it, so an older answer can't land after
    // the newer one and leave the header on a schedule that has since changed (T41853).
    private metricsRequest: Subscription = null;

    @Input() campaign: Campaign;
    @Input() step: number;
    @Input() metrics: any;

    constructor(
        private campaignService: CampaignService,
        private authenticationService: AuthenticationService,
        @Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef
    ) { }

    ngOnInit() {
        this.updateStepStates();
        this.loadCachedHighlights();
    }

    ngOnChanges() {
        this.updateStepStates();
        if (this.metrics) {
            // Step 2 supplies freshly-computed metrics directly — no backend round-trip.
            this.updateHighlights(this.metrics);
            this.lastMetricsSignature = this.scheduleSignature();
        } else if (this.campaign != null) {
            // Steps 3-5: only re-fetch when the schedule actually changed since the last fetch.
            // Plain step navigation leaves the signature unchanged, so we keep the current numbers.
            const signature = this.scheduleSignature();
            if (signature !== this.lastMetricsSignature) {
                this.lastMetricsSignature = signature;
                this.getCampaignMetrics();
            }
        }
    }

    /**
     * The parts of the campaign that actually drive the metrics: its channels and each product's
     * channels + day-parting (booked slots), plus the billing method. Anything else changing (e.g.
     * just moving between steps) must not trigger a recompute.
     */
    private scheduleSignature(): string {
        const c: any = this.campaign;
        if (!c) {
            return '';
        }
        const products = (c.products || []).map((p: any) => ({
            channels: p.channels || [],
            dayPartings: (p.dayPartings || []).map((d: any) => ({ date: d.date, slotHex: d.slotHex }))
        }));
        return JSON.stringify({
            id: c._id,
            billingMethod: c.billingMethod,
            channels: c.channels || [],
            products
        });
    }

    updateStepStates() {
        this.isLocationScreensGreyedOut = this.step > this.SELECTION_END_STEP;
        this.isSlotPlaysGreyedOut = this.step < this.METRICS_START_STEP;
        this.isBudgetPriceGreyedOut = this.step < this.BUDGET_START_STEP;
    }

    getCampaignMetrics() {
        this.isLoading = true;
        if (this.metricsRequest) {
            this.metricsRequest.unsubscribe();
        }
        this.metricsRequest = this.campaignService.getCampaignMetrics(this.campaign).subscribe((resp: any) => {
            this.updateHighlights(resp);
            this.isLoading = false;
        });
    }

    ngOnDestroy() {
        if (this.metricsRequest) {
            this.metricsRequest.unsubscribe();
        }
    }

    updateHighlights(metrics: any) {
        if (!metrics) return;

        const newData = this.mapMetrics(metrics);

        // Always take the freshly calculated locations/screens, on every step. Previously steps 3-5
        // kept the cached values, so a channel deactivated while the client was past step 2 stayed
        // counted in the highlight box until they navigated back to step 2.
        const updatedState = {
            ...this.highlights,
            ...newData,
            campaignBudget: {
                ...this.highlights.campaignBudget,
                value: this.campaign?.budget || 0
            }
        };
        this.highlights = updatedState;
        if (this.campaign?._id) {
            this.campaignService.cachedHighlights.set(this.campaign._id, { ...updatedState });
        }

        this.changeDetectorRef.detectChanges();
    }

    private loadCachedHighlights() {
        if (this.campaign?._id && this.campaignService.cachedHighlights.has(this.campaign._id)) {
            this.highlights = { ...this.campaignService.cachedHighlights.get(this.campaign._id) };
        }
    }

    private mapMetrics(m: any) {
        return {
            locations: m.locations,
            channels: m.channels,
            screens: m.screens,
            slots: m.slots,
            plays: m.plays,
            totalImpressions: m.totalImpressions,
            totalAttentiveImpressions: m.totalAttentiveImpressions,
            totalReaches: m.totalReaches,
            externalMetricsSource: !!m.externalMetricsSource,
            avgCPM: m.avgCPM,
            campaignTotalPrice: m.campaignTotalPrice,
            nonCappedCampaignTotalPrice: m.nonCappedCampaignTotalPrice,
            externalData: m.externalData
        };
    }
}
